import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import { query } from '../client';

// ============ Types ============

export interface Article {
  id: string;
  title: string;
  content: string;
  excerpt?: string | null;
  category: string;
  cover_image?: string | null;
  language: string;
  published?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface VideoFile {
  id: string;
  title: string;
  description?: string | null;
  video_url?: string | null;
  cover_image?: string | null;
  duration?: number | null;
  category: string;
  language: string;
  is_vr?: boolean | null;
  vr_projection?: string | null;
  vr_stereo_mode?: string | null;
  published?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AudioFile {
  id: string;
  title: string;
  description?: string | null;
  audio_url?: string | null;
  cover_image?: string | null;
  duration?: number | null;
  category: string;
  language: string;
  published?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Assessment {
  id: string;
  title: string;
  description?: string | null;
  cover_image?: string | null;
  language: string;
  published?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ContentInfo {
  title: string;
  description?: string | null;
  duration?: number | null;
}

// ============ Queries ============

export async function getArticles(language: string, productId?: string): Promise<ApiResult<Article[]>> {
  if (productId) {
    // Filter by product using junction table
    const { data: productContent, error: junctionError } = await supabase
      .from('product_content')
      .select('content_id')
      .eq('product_id', productId)
      .eq('content_type', 'article');

    if (junctionError) {
      console.error('[getArticles] Junction query error:', junctionError);
      return err(ApiError.fromSupabaseError(junctionError));
    }

    // content_id is UUID, extract as strings for the IN query
    const articleIds = (productContent || []).map(pc => pc.content_id).filter(Boolean);
    
    console.log('[getArticles] Product:', productId, 'Language:', language, 'IDs found:', articleIds.length);

    if (articleIds.length === 0) {
      return ok([]);
    }

    const result = await query(async () =>
      supabase
        .from('articles')
        .select('*')
        .in('id', articleIds)
        .eq('language', language)
        .eq('published', true)
        .order('created_at', { ascending: false })
    );
    
    console.log('[getArticles] Articles returned:', result.success ? result.data.length : 'error');
    return result;
  }

  // No product filter - return all articles
  return query(async () =>
    supabase
      .from('articles')
      .select('*')
      .eq('language', language)
      .eq('published', true)
      .order('created_at', { ascending: false })
  );
}

export async function getArticleById(id: string): Promise<ApiResult<Article>> {
  return query(async () =>
    supabase
      .from('articles')
      .select('*')
      .eq('id', id)
      .single()
  );
}

export async function getVideos(language: string, productId?: string): Promise<ApiResult<VideoFile[]>> {
  if (productId) {
    // Filter by product using junction table
    const { data: productContent, error: junctionError } = await supabase
      .from('product_content')
      .select('content_id')
      .eq('product_id', productId)
      .eq('content_type', 'video');

    if (junctionError) {
      return err(ApiError.fromSupabaseError(junctionError));
    }

    const videoIds = productContent?.map(pc => pc.content_id) || [];

    if (videoIds.length === 0) {
      return ok([]);
    }

    return query(async () =>
      supabase
        .from('video_files')
        .select('*')
        .in('id', videoIds)
        .eq('language', language)
        .eq('published', true)
        .order('created_at', { ascending: false })
    );
  }

  // No product filter - return all videos
  return query(async () =>
    supabase
      .from('video_files')
      .select('*')
      .eq('language', language)
      .eq('published', true)
      .order('created_at', { ascending: false })
  );
}

export async function getVideoById(id: string): Promise<ApiResult<VideoFile>> {
  return query(async () =>
    supabase
      .from('video_files')
      .select('*')
      .eq('id', id)
      .single()
  );
}

export async function getAudioFiles(language: string, productId?: string): Promise<ApiResult<AudioFile[]>> {
  if (productId) {
    // Filter by product using junction table
    const { data: productContent, error: junctionError } = await supabase
      .from('product_content')
      .select('content_id')
      .eq('product_id', productId)
      .eq('content_type', 'audio');

    if (junctionError) {
      return err(ApiError.fromSupabaseError(junctionError));
    }

    const audioIds = productContent?.map(pc => pc.content_id) || [];

    if (audioIds.length === 0) {
      return ok([]);
    }

    return query(async () =>
      supabase
        .from('audio_files')
        .select('*')
        .in('id', audioIds)
        .eq('language', language)
        .eq('published', true)
        .order('created_at', { ascending: false })
    );
  }

  // No product filter - return all audio files
  return query(async () =>
    supabase
      .from('audio_files')
      .select('*')
      .eq('language', language)
      .eq('published', true)
      .order('created_at', { ascending: false })
  );
}

export async function getAudioById(id: string): Promise<ApiResult<AudioFile>> {
  return query(async () =>
    supabase
      .from('audio_files')
      .select('*')
      .eq('id', id)
      .single()
  );
}

export async function getAssessments(language: string, productId?: string): Promise<ApiResult<Assessment[]>> {
  if (productId) {
    // Filter by product using junction table
    const { data: productContent, error: junctionError } = await supabase
      .from('product_content')
      .select('content_id')
      .eq('product_id', productId)
      .eq('content_type', 'assessment');

    if (junctionError) {
      return err(ApiError.fromSupabaseError(junctionError));
    }

    const assessmentIds = productContent?.map(pc => pc.content_id) || [];

    if (assessmentIds.length === 0) {
      return ok([]);
    }

    return query(async () =>
      supabase
        .from('assessments')
        .select('*')
        .in('id', assessmentIds)
        .eq('language', language)
        .eq('published', true)
        .order('created_at', { ascending: false })
    );
  }

  // No product filter - return all assessments
  return query(async () =>
    supabase
      .from('assessments')
      .select('*')
      .eq('language', language)
      .eq('published', true)
      .order('created_at', { ascending: false })
  );
}

export async function getAssessmentById(id: string): Promise<ApiResult<Assessment>> {
  return query(async () =>
    supabase
      .from('assessments')
      .select('*')
      .eq('id', id)
      .single()
  );
}

/**
 * Batch fetch content info for multiple activity IDs grouped by type
 */
export async function batchGetContentInfo(
  activityIds: Array<{ type: string; id: string }>
): Promise<ApiResult<Map<string, ContentInfo>>> {
  const articleIds = activityIds.filter(a => a.type === 'article').map(a => a.id);
  const videoIds = activityIds.filter(a => a.type === 'video').map(a => a.id);
  const audioIds = activityIds.filter(a => a.type === 'audio').map(a => a.id);
  const assessmentIds = activityIds.filter(a => a.type === 'assessment').map(a => a.id);

  try {
    const [articles, videos, audios, assessments] = await Promise.all([
      articleIds.length > 0 
        ? supabase.from('articles').select('id, title, excerpt').in('id', articleIds)
        : Promise.resolve({ data: [], error: null }),
      videoIds.length > 0
        ? supabase.from('video_files').select('id, title, description, duration').in('id', videoIds)
        : Promise.resolve({ data: [], error: null }),
      audioIds.length > 0
        ? supabase.from('audio_files').select('id, title, description, duration').in('id', audioIds)
        : Promise.resolve({ data: [], error: null }),
      assessmentIds.length > 0
        ? supabase.from('assessments').select('id, title, description').in('id', assessmentIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const contentMap = new Map<string, ContentInfo>();

    const allItems = [
      ...(articles.data || []).map(a => ({ ...a, description: a.excerpt })),
      ...(videos.data || []),
      ...(audios.data || []),
      ...(assessments.data || []),
    ];

    for (const item of allItems) {
      contentMap.set(item.id, {
        title: item.title,
        description: (item as { description?: string }).description || null,
        duration: (item as { duration?: number }).duration || null,
      });
    }

    return ok(contentMap);
  } catch (error) {
    console.error('Error batch fetching content:', error);
    return err(ApiError.networkError());
  }
}
